import React, { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Skeleton from '@material-ui/lab/Skeleton';

import Icon from 'components/atoms/Icon';

const useStyles = makeStyles((theme) => ({
  tableHeadRow: {
    '& > th:first-child': {
      zIndex: theme.zIndex.table.headFirst,
      borderRight: '1px solid #efefef',
    },
  },
  tableHeadCell: {
    position: 'sticky',
    top: 0,
    left: 0,
    zIndex: theme.zIndex.table.head,
    flexDirection: 'row',
    padding: '5px !important',
    backgroundColor: theme.palette.table.headCellBg,
    borderTop: '1px solid rgba(224,224,224,1)',
    color: theme.palette.table.text,
    cursor: 'pointer',
    wordBreak: 'keep-all',
    fontSize: 12,
    '&:first-child': {
      zIndex: 23,
      // borderLeft: theme.palette.table.border.body,
    },
    '&:not(:last-child)': { borderRight: theme.palette.box.border },
  },
  orderIco: { '& svg': { opacity: 0.4 }, '&:hover svg': { opacity: 1 } },
  sortIcon: {
    marginLeft: 8,
    transition: 'all ease 0.4s',
    '&.active svg': { color: theme.palette.table.text, opacity: 1 },
    '&.asc': { transform: 'rotate(180deg)' },
    '&.desc': { transform: 'rotate(0deg)' },
  },
  funcCell: {
    display: 'flex',
    alignItems: 'center',
    '& > div': { width: '100%', '& >  span': { display: 'block' } },
  },
  checkboxRoot: { padding: 0 },
}));

const EnhancedTableHead = (props) => {
  const {
    loading,
    order,
    sort,
    onRequestSort,
    allCheck,
    newSelected,
    list,
    headCells,
    useCheckBox,
    customized = {},
    expandTable,
    handleHeadClick,
    tableKey = null,
  } = props;
  const classes = useStyles();

  const [checked, setChecked] = useState(false);

  const columnCnt = Array.isArray(headCells)
    ? headCells.length
    : headCells.size === undefined
    ? 0
    : headCells.size;

  useEffect(() => {
    if (allCheck) setChecked(true);
  }, [allCheck]);

  useEffect(() => {
    if (list.length !== newSelected.length) setChecked(false);
    else setChecked(true);
  }, [newSelected, list]);

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  /**
   * 헤드컬럼생성함수
   * @type {function(): []}
   */
  const makeHeadCell = useCallback(() => {
    const result = [];
    for (let headCell of headCells.values()) {
      const { id, disablePadding, label, className } = headCell;

      let { orderFlag } = headCell;
      let customOrder = null;
      if (id !== 'SEQ') {
        let content = label;
        if (typeof customized[id] !== 'undefined') {
          if (customized[id].merged === true) {
            continue;
          } else if (typeof customized[id].makeHead !== 'undefined') {
            const result = customized[id].makeHead(headCells, tableKey);
            if (result.content !== undefined) {
              content = result.content;
              orderFlag = result.orderFlag;
            } else {
              content = result;
            }
          }
          if (typeof customized[id].orderFlag !== 'undefined') {
            customOrder = customized[id].orderFlag;
          } else {
            customOrder = null;
          }
        }
        result.push(
          <TableCell
            style={
              customized[id] !== undefined ? customized[id].headStyle : null
            }
            className={className}
            classes={{ root: classes.tableHeadCell }}
            key={id}
            align="center"
            padding={disablePadding ? 'none' : 'default'}
            sortDirection={sort === id ? sort : false}
          >
            {customOrder === true ||
            (customOrder === null && orderFlag === true) ? (
              <TableSortLabel
                className={classes.orderIco}
                active={order === id}
                direction={order === id ? sort : 'desc'}
                onClick={createSortHandler(id)}
                IconComponent={() => {
                  return (
                    <div
                      className={clsx(classes.sortIcon, {
                        active: order === id,
                        desc: sort === 'desc',
                        asc: order === id && sort === 'asc',
                      })}
                    >
                      <Icon icon="faCaretDown" color="default" />
                    </div>
                  );
                }}
              >
                {content}
              </TableSortLabel>
            ) : (
              <>{content}</>
            )}
          </TableCell>
        );
      }
    }
    return result;
    // eslint-disable-next-line
  }, [
    headCells,
    customized,
    sort,
    order,
    onRequestSort,
    tableKey,
    createSortHandler,
    classes.sortIcon,
    classes.tableHeadCell,
    classes.orderIco,
  ]);

  const handleClick = () => {
    let newChecked = checked;
    newChecked = newChecked === false;
    setChecked(newChecked);
    handleHeadClick(newChecked);
  };

  return (
    <TableHead>
      <TableRow>
        {loading ? (
          <TableCell
            colSpan={columnCnt + 1}
            className={clsx(classes.tableHeadCell)}
          >
            <Skeleton />
          </TableCell>
        ) : (
          <>
            {loading === false && (useCheckBox || expandTable) && (
              <>
                <TableCell
                  style={{ width: 30 }}
                  className={clsx(classes.tableHeadCell)}
                >
                  <div className={classes.funcCell}>
                    {useCheckBox && (
                      <div>
                        <Checkbox
                          classes={{ root: classes.checkboxRoot }}
                          indeterminate={
                            newSelected.length > 0 && checked === false
                          }
                          checked={
                            checked &&
                            list.length !== 0 &&
                            list.length === newSelected.length
                          }
                          onChange={handleClick}
                          inputProps={{ 'aria-label': 'select all desserts' }}
                          color="primary"
                        />
                      </div>
                    )}
                    {expandTable && <div style={{ minWidth: 28 }} />}
                  </div>
                </TableCell>
              </>
            )}
            {makeHeadCell()}
          </>
        )}
      </TableRow>
    </TableHead>
  );
};

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
};

export default React.memo(withRouter(EnhancedTableHead));
